import {isNumber} from 'utils/strings'
import lodash from 'lodash'

// Only assume flat props objects. Does not support nested props object.
export const isPropsObjectEqual = <T extends Record<string, unknown>>(next: T | undefined, prev: T | undefined): boolean => {
    if (Object.keys(next ?? {}).length !== Object.keys(prev ?? {}).length) {
        return false
    }
    const keys = Object.keys(next ?? {}) as Array<keyof T>
    for (const key of keys) {
        if (next?.[key] !== prev?.[key]) {
            return false
        }
    }
    return true
}

export const isEqual = <T>(next: T | undefined, prev: T | undefined): boolean => {
    const isEmptyOrNil = (value: T | undefined): boolean => lodash.isNil(value) || lodash.isUndefined(value) || lodash.isEmpty(value)
    if (isEmptyOrNil(next) && isEmptyOrNil(prev)) {
        return true
    }
    return lodash.isEqual(next, prev)
}


export const isArrayEqual = <T>(next?: Array<T>, prev?: Array<T>): boolean => {
    if (next?.length !== prev?.length) {
        return false
    }
    return next?.every((item) => prev?.includes(item)) ?? true
}

export const isObjectEmpty = <T>(obj?: T | undefined): boolean => Object.values(obj ?? {}).every((value) => lodash.isNil(value) || lodash.isEmpty(value) || lodash.isUndefined(value))


export const sortNestedObject = <T>(input: T): T => {
    // If the input is an array, recursively sort each element.
    if (Array.isArray(input)) {
        return input.map((item) => sortNestedObject(item)) as unknown as T
    }

    // If the input is an object, sort the keys and recursively sort each value.
    if (!!input && typeof input === 'object') {
        const sortedInput = Object.keys(input)
            .sort((a, b) => {
                if (isNumber(a) && isNumber(b)) {
                    return Number(a) - Number(b)
                } else if (isNumber(a)) {
                    return -1
                } else if (isNumber(b)) {
                    return 1
                }
                return a < b ? -1 : 1
            })
            .reduce((acc, key) => {
                acc[key] = sortNestedObject(input?.[key as keyof typeof input])
                return acc
            }, {} as Record<string, unknown>)
        return sortedInput as T
    }

    return input
}

export const cleanObject = <T>(obj: T) => Object.fromEntries(
    Object.entries(obj ?? {})
        .filter(
            ([, v]) => v !== null && v !== undefined && v !== '',
        ),
)
